import React, { useState, useEffect, useCallback } from 'react';
import { revenueSourceAPI, revenueAPI } from '../services/api';
import api from '../services/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  TrendingUp, 
  Layers,
  DollarSign,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Wallet,
  Zap
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#22C55E', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899'];

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const PERFIS_RECEBIMENTO = [
  { value: 'a_vista', label: 'À Vista', description: 'Recebimento imediato' },
  { value: '30_dias', label: '30 dias', description: 'Recebimento após 30 dias' },
  { value: '60_dias', label: '60 dias', description: 'Recebimento após 60 dias' },
  { value: 'parcelado', label: 'Parcelado', description: 'Recebimento em parcelas' },
  { value: 'recorrente', label: 'Recorrente Mensal', description: 'Recebimento mensal automático' }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Descreve o perfil de recebimento de forma completa, incluindo parcelas e antecipação
const descreverPerfil = (perfilValue, numeroParcelas, antecipado) => {
  const base = PERFIS_RECEBIMENTO.find(p => p.value === perfilValue)?.label || 'À Vista';
  if (perfilValue === 'parcelado') {
    const n = numeroParcelas || 2;
    return antecipado
      ? `Parcelado ${n}x (antecipado - cai à vista)`
      : `Parcelado ${n}x (sem antecipação)`;
  }
  return base;
};

const monthOrder = {
  'janeiro': 1, 'fevereiro': 2, 'março': 3, 'abril': 4, 'maio': 5, 'junho': 6,
  'julho': 7, 'agosto': 8, 'setembro': 9, 'outubro': 10, 'novembro': 11, 'dezembro': 12
};

// Índice absoluto de mês (ano*12 + mês) para medir tempo decorrido
const _mesIdxHoje = () => { const d = new Date(); return d.getFullYear() * 12 + d.getMonth(); };
const _mesIdxVenda = (r) => (r.year || 0) * 12 + ((monthOrder[(r.month || '').toLowerCase()] || 1) - 1);

// Quanto de uma venda JÁ foi recebido, considerando o perfil de recebimento e o tempo decorrido.
// Regra alinhada ao backend (/gap-detalhado): a_vista/recorrente = imediato; 30 dias = +1 mês;
// 60 dias = +2 meses; parcelado Nx = 1/N por mês (antecipado = tudo à vista).
// O "recebido real" informado manualmente sempre prevalece.
const calcRecebidoVenda = (r) => {
  const amount = r.amount || 0;
  if (r.recebido_real !== null && r.recebido_real !== undefined && r.recebido_real !== '') {
    return parseFloat(r.recebido_real) || 0;
  }
  const perfil = r.perfil_recebimento || 'a_vista';
  const decorridos = _mesIdxHoje() - _mesIdxVenda(r); // meses desde a venda
  if (perfil === 'a_vista' || perfil === 'recorrente') return amount;
  if (perfil === 'parcelado') {
    if (r.antecipado) return amount;
    const n = r.numero_parcelas || 1;
    if (n <= 1) return amount;
    const pagas = Math.max(0, Math.min(n, decorridos + 1)); // 1ª parcela no mês da venda
    return amount * (pagas / n);
  }
  const delay = perfil === '30_dias' ? 1 : perfil === '60_dias' ? 2 : 0;
  return decorridos >= delay ? amount : 0;
};

export const GestaoReceitaPage = () => {
  const { selectedGym } = useGym();
  const { isOnboardingActive, currentStepData } = useOnboarding();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vendas');
  
  // Dados
  const [revenues, setRevenues] = useState([]);
  const [sources, setSources] = useState([]);
  const [types, setTypes] = useState([]);
  
  // Filtros
  const [filtroMes, setFiltroMes] = useState(MESES[new Date().getMonth()]);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  
  // Modal de venda/faturamento
  const [showVendaModal, setShowVendaModal] = useState(false);
  const [editingVenda, setEditingVenda] = useState(null);
  const [vendaForm, setVendaForm] = useState({
    source_type: '',
    amount: '',
    description: '',
    perfil_recebimento: 'a_vista',
    numero_parcelas: 2,
    antecipado: false,
    recebido_real: '',
    data_recebimento: ''
  });
  
  // Modal de tipo/fonte
  const [showTipoModal, setShowTipoModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [tipoForm, setTipoForm] = useState({
    name: '',
    color: '#22C55E',
    perfil_padrao: 'a_vista',
    numero_parcelas_padrao: 2,
    antecipado_padrao: false
  });

  // Modal de recebido real (correção manual de meses fechados)
  const [recebidos, setRecebidos] = useState([]);
  const [recebidoCalc, setRecebidoCalc] = useState([]);
  const [showRecebidoModal, setShowRecebidoModal] = useState(false);
  const [recebidoForm, setRecebidoForm] = useState({
    mes: '',
    ano: new Date().getFullYear(),
    valor_recebido: '',
    fonte: '',
    observacao: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedGym?.id) return;
    setLoading(true);
    try {
      const [revenuesRes, sourcesRes, typesRes, recebidosRes, recebidoCalcRes] = await Promise.all([
        revenueAPI.getAll(selectedGym.id),
        revenueSourceAPI.getAll(selectedGym.id),
        revenueSourceAPI.getTypes(selectedGym.id),
        api.get('/recebido-real', { params: { gym_id: selectedGym.id } }),
        api.get('/recebido-real-calculado', { params: { gym_id: selectedGym.id } })
      ]);
      setRevenues(revenuesRes.data);
      setSources(sourcesRes.data);
      setTypes(typesRes.data);
      setRecebidos(recebidosRes.data);
      setRecebidoCalc(recebidoCalcRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [selectedGym?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Onboarding: no passo "parcelamento" do tour, abre o modal de venda com o simulador
  // de parcelas/antecipação já visível. Fecha ao sair do passo.
  useEffect(() => {
    if (isOnboardingActive && currentStepData?.id === 'parcelamento') {
      setVendaForm(prev => ({ ...prev, perfil_recebimento: 'parcelado', numero_parcelas: 3, antecipado: false }));
      setShowVendaModal(true);
    } else if (isOnboardingActive) {
      setShowVendaModal(false);
    }
  }, [isOnboardingActive, currentStepData?.id]);

  // Filtrar vendas pelo mês/ano
  const vendasFiltradas = revenues.filter(r => 
    r.month?.toLowerCase() === filtroMes.toLowerCase() && r.year === filtroAno
  );

  // Totais (Recebido/A Receber consideram o perfil de recebimento e o tempo decorrido)
  const totalVendas = vendasFiltradas.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalRecebido = vendasFiltradas.reduce((sum, r) => sum + calcRecebidoVenda(r), 0);
  const totalAReceber = totalVendas - totalRecebido;

  // Agrupar por fonte
  const vendasPorFonte = vendasFiltradas.reduce((acc, r) => {
    const fonte = r.source_type || 'Outros';
    if (!acc[fonte]) acc[fonte] = { vendido: 0, recebido: 0 };
    acc[fonte].vendido += r.amount || 0;
    acc[fonte].recebido += calcRecebidoVenda(r);
    return acc;
  }, {});

  // Dados para gráficos
  const chartDataPie = Object.entries(vendasPorFonte).map(([name, data], index) => ({
    name,
    value: data.vendido,
    color: types.find(t => t.name === name)?.color || COLORS[index % COLORS.length]
  }));

  const chartDataBar = Object.entries(vendasPorFonte).map(([name, data]) => ({
    name: name.length > 15 ? name.substring(0, 12) + '...' : name,
    Vendido: data.vendido,
    Recebido: data.recebido
  }));

  // Handlers de Venda
  const handleSaveVenda = async () => {
    if (!vendaForm.amount) {
      toast.error('Informe o valor da venda');
      return;
    }
    try {
      const payload = {
        gym_id: selectedGym.id,
        month: filtroMes,
        year: filtroAno,
        amount: parseFloat(vendaForm.amount),
        source_type: vendaForm.source_type || null,
        description: vendaForm.description || vendaForm.source_type || 'Venda',
        perfil_recebimento: vendaForm.perfil_recebimento,
        numero_parcelas: vendaForm.perfil_recebimento === 'parcelado' ? (parseInt(vendaForm.numero_parcelas) || 2) : 1,
        antecipado: vendaForm.perfil_recebimento === 'parcelado' ? !!vendaForm.antecipado : false,
        recebido_real: vendaForm.recebido_real ? parseFloat(vendaForm.recebido_real) : null,
        data_recebimento: vendaForm.data_recebimento || null
      };
      
      if (editingVenda) {
        await revenueAPI.update(editingVenda.id, payload);
        toast.success('Venda atualizada');
      } else {
        await revenueAPI.create(payload);
        toast.success('Venda cadastrada');
      }
      setShowVendaModal(false);
      setEditingVenda(null);
      resetVendaForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar venda');
    }
  };

  const handleEditVenda = (venda) => {
    setEditingVenda(venda);
    setVendaForm({
      source_type: venda.source_type || '',
      amount: venda.amount?.toString() || '',
      description: venda.description || '',
      perfil_recebimento: venda.perfil_recebimento || 'a_vista',
      numero_parcelas: venda.numero_parcelas || 2,
      antecipado: venda.antecipado || false,
      recebido_real: venda.recebido_real?.toString() || '',
      data_recebimento: venda.data_recebimento || ''
    });
    setShowVendaModal(true);
  };

  const handleDeleteVenda = async (id) => {
    if (!window.confirm('Excluir esta venda?')) return;
    try {
      await revenueAPI.delete(id);
      toast.success('Venda excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetVendaForm = () => {
    setVendaForm({
      source_type: '',
      amount: '',
      description: '',
      perfil_recebimento: 'a_vista',
      numero_parcelas: 2,
      antecipado: false,
      recebido_real: '',
      data_recebimento: ''
    });
  };

  // Handlers de Tipo/Fonte
  const handleSaveTipo = async () => {
    if (!tipoForm.name) {
      toast.error('Informe o nome da fonte');
      return;
    }
    try {
      const payload = {
        gym_id: selectedGym.id,
        name: tipoForm.name,
        color: tipoForm.color,
        perfil_padrao: tipoForm.perfil_padrao,
        numero_parcelas_padrao: tipoForm.perfil_padrao === 'parcelado' ? (parseInt(tipoForm.numero_parcelas_padrao) || 2) : 1,
        antecipado_padrao: tipoForm.perfil_padrao === 'parcelado' ? !!tipoForm.antecipado_padrao : false
      };
      
      if (editingTipo) {
        await revenueSourceAPI.updateType(editingTipo.id, payload);
        toast.success('Fonte atualizada');
      } else {
        await revenueSourceAPI.createType(payload);
        toast.success('Fonte cadastrada');
      }
      setShowTipoModal(false);
      setEditingTipo(null);
      resetTipoForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar fonte');
    }
  };

  const handleEditTipo = (tipo) => {
    setEditingTipo(tipo);
    setTipoForm({
      name: tipo.name || '',
      color: tipo.color || '#22C55E',
      perfil_padrao: tipo.perfil_padrao || 'a_vista',
      numero_parcelas_padrao: tipo.numero_parcelas_padrao || 2,
      antecipado_padrao: tipo.antecipado_padrao || false
    });
    setShowTipoModal(true);
  };

  const handleDeleteTipo = async (id) => {
    if (!window.confirm('Excluir esta fonte de receita?')) return;
    try {
      await revenueSourceAPI.deleteType(id);
      toast.success('Fonte excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetTipoForm = () => {
    setTipoForm({
      name: '',
      color: '#22C55E',
      perfil_padrao: 'a_vista',
      numero_parcelas_padrao: 2,
      antecipado_padrao: false
    });
  };

  // Handlers de Recebido Real (correção manual de meses fechados)
  const handleSaveRecebido = async () => {
    if (!recebidoForm.mes || !recebidoForm.valor_recebido) {
      toast.error('Informe o mês e o valor recebido');
      return;
    }
    try {
      const payload = {
        gym_id: selectedGym.id,
        mes: recebidoForm.mes,
        ano: parseInt(recebidoForm.ano),
        valor_recebido: parseFloat(recebidoForm.valor_recebido),
        fonte: recebidoForm.fonte,
        observacao: recebidoForm.observacao
      };
      await api.post('/recebido-real', payload);
      toast.success('Recebido real registrado');
      setShowRecebidoModal(false);
      resetRecebidoForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar recebido real');
    }
  };

  const handleDeleteRecebido = async (id) => {
    if (!window.confirm('Excluir este registro de recebido real?')) return;
    try {
      await api.delete(`/recebido-real/${id}`);
      toast.success('Registro excluído');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetRecebidoForm = () => {
    setRecebidoForm({ mes: '', ano: new Date().getFullYear(), valor_recebido: '', fonte: '', observacao: '' });
  };

  if (!selectedGym) {
    return (
      <DashboardLayout>
        <div>
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-8 text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" style={{ color: 'var(--color-primary)' }} />
              <p style={{ color: 'var(--app-text-secondary)' }}>Selecione uma empresa para gerenciar receitas</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="gestao-receita-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--app-text-primary)' }}>
              Gestão de Receita
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--app-text-secondary)' }}>
              Gerencie vendas, fontes de receita e acompanhe recebimentos
            </p>
          </div>
          
          {/* Filtros */}
          <div className="flex gap-2 flex-wrap">
            <Select value={filtroMes} onValueChange={setFiltroMes}>
              <SelectTrigger className="w-[140px]" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                {MESES.map((m) => (
                  <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filtroAno.toString()} onValueChange={(v) => setFiltroAno(parseInt(v))}>
              <SelectTrigger className="w-[100px]" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                {[2023, 2024, 2025, 2026].map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                  <TrendingUp className="text-green-500" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase" style={{ color: 'var(--app-text-muted)' }}>Total Vendido</p>
                  <p className="text-xl font-bold text-green-500">{formatCurrency(totalVendas)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <CheckCircle2 className="text-blue-500" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase" style={{ color: 'var(--app-text-muted)' }}>Total Recebido</p>
                  <p className="text-xl font-bold text-blue-500">{formatCurrency(totalRecebido)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }}>
                  <Clock className="text-orange-500" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase" style={{ color: 'var(--app-text-muted)' }}>A Receber</p>
                  <p className="text-xl font-bold text-orange-500">{formatCurrency(totalAReceber)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Nota explicativa */}
        <Card style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', borderColor: 'rgba(59, 130, 246, 0.2)' }}>
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Info className="text-blue-500 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--app-text-primary)' }}>
                  Competencia vs. Caixa
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-secondary)' }}>
                  As vendas aqui representam o <strong>faturamento por competencia</strong> (o que pertence ao mes).
                  O <strong>recebido real</strong> pode ser informado quando houver antecipacoes ou divergencias.
                  Para ver o dinheiro que realmente entrou no banco, consulte a tela de <strong>Caixa</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3" style={{ backgroundColor: 'var(--app-card-bg)' }}>
            <TabsTrigger value="vendas" data-testid="tab-vendas">Vendas / Faturamento</TabsTrigger>
            <TabsTrigger value="fontes" data-testid="tab-fontes">Fontes de Receita</TabsTrigger>
            <TabsTrigger value="recebido" data-testid="tab-recebido">Recebido Real</TabsTrigger>
          </TabsList>

          {/* Tab Vendas */}
          <TabsContent value="vendas" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--app-text-primary)' }}>
                Vendas de {filtroMes.charAt(0).toUpperCase() + filtroMes.slice(1)}/{filtroAno}
              </h2>
              <Button 
                onClick={() => setShowVendaModal(true)}
                style={{ backgroundColor: 'var(--color-primary)' }}
                data-testid="add-venda-btn"
              >
                <Plus size={16} className="mr-2" /> Nova Venda
              </Button>
            </div>

            {/* Gráficos */}
            {chartDataPie.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                      Distribuicao por Fonte
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={chartDataPie}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {chartDataPie.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                      Vendido vs Recebido
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={chartDataBar}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--app-border)" />
                        <XAxis dataKey="name" tick={{ fill: 'var(--app-text-secondary)', fontSize: 10 }} />
                        <YAxis tick={{ fill: 'var(--app-text-secondary)', fontSize: 10 }} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}
                        />
                        <Bar dataKey="Vendido" fill="#22C55E" />
                        <Bar dataKey="Recebido" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Lista de Vendas */}
            <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
              <CardContent className="p-4">
                {loading ? (
                  <p className="text-center py-8" style={{ color: 'var(--app-text-muted)' }}>Carregando...</p>
                ) : vendasFiltradas.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: 'var(--app-text-muted)' }} />
                    <p style={{ color: 'var(--app-text-muted)' }}>Nenhuma venda cadastrada neste periodo</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {vendasFiltradas.map((venda) => {
                      const tipo = types.find(t => t.name === venda.source_type);
                      const cor = tipo?.color || '#22C55E';
                      const temDiferenca = venda.recebido_real && venda.recebido_real !== venda.amount;
                      
                      return (
                        <div 
                          key={venda.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border gap-3"
                          style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: `${cor}20`, color: cor }}
                              >
                                {venda.source_type || 'Outros'}
                              </span>
                              <span className="text-sm font-medium" style={{ color: 'var(--app-text-primary)' }}>
                                {venda.description || 'Venda'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                              <span>{descreverPerfil(venda.perfil_recebimento, venda.numero_parcelas, venda.antecipado)}</span>
                              {temDiferenca && (
                                <span className="flex items-center gap-1 text-orange-500">
                                  <AlertCircle size={12} />
                                  Recebido diferente
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-green-500">{formatCurrency(venda.amount)}</p>
                              {venda.recebido_real && (
                                <p className="text-xs text-blue-500">
                                  Recebido: {formatCurrency(venda.recebido_real)}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleEditVenda(venda)}
                                data-testid={`edit-venda-${venda.id}`}
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-red-500"
                                onClick={() => handleDeleteVenda(venda.id)}
                                data-testid={`delete-venda-${venda.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Fontes */}
          <TabsContent value="fontes" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--app-text-primary)' }}>
                Fontes / Linhas de Receita
              </h2>
              <Button 
                onClick={() => setShowTipoModal(true)}
                style={{ backgroundColor: 'var(--color-primary)' }}
                data-testid="add-fonte-btn"
              >
                <Plus size={16} className="mr-2" /> Nova Fonte
              </Button>
            </div>

            <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
              <CardContent className="p-4">
                {types.length === 0 ? (
                  <div className="text-center py-8">
                    <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: 'var(--app-text-muted)' }} />
                    <p style={{ color: 'var(--app-text-muted)' }}>Nenhuma fonte cadastrada</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                      Crie fontes como "Mensalidades", "Avulsas", "Produtos" etc.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {types.map((tipo) => (
                      <div 
                        key={tipo.id || tipo.name}
                        className="flex items-center justify-between p-4 rounded-lg border"
                        style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: tipo.color || '#22C55E' }}
                          />
                          <div>
                            <p className="font-medium" style={{ color: 'var(--app-text-primary)' }}>{tipo.name}</p>
                            <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                              Perfil: {descreverPerfil(tipo.perfil_padrao, tipo.numero_parcelas_padrao, tipo.antecipado_padrao)}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => handleEditTipo(tipo)}
                            data-testid={`edit-tipo-${tipo.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-red-500"
                            onClick={() => handleDeleteTipo(tipo.id)}
                            data-testid={`delete-tipo-${tipo.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Recebido Real */}
          <TabsContent value="recebido" className="space-y-4">
            <div className="flex flex-wrap justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold" style={{ color: 'var(--app-text-primary)' }}>
                  Recebido Real (automático via OFX)
                </h2>
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                  Aparece automaticamente conforme você classifica entradas no <strong>Caixa</strong> como "Recebimento Operacional" (aportes e empréstimos ficam de fora). Só precisa preencher manualmente se o mês NÃO tem OFX importado ainda.
                </p>
              </div>
              <Button
                onClick={() => setShowRecebidoModal(true)}
                style={{ backgroundColor: 'var(--color-primary)' }}
                data-testid="add-recebido-btn"
              >
                <Plus size={16} className="mr-2" /> Override Manual
              </Button>
            </div>

            <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
              <CardContent className="p-4 md:p-6" data-testid="recebido-calc-table">
                {recebidoCalc.length === 0 ? (
                  <p className="text-sm text-center py-8" style={{ color: 'var(--app-text-muted)' }}>
                    Nenhum dado ainda. Cadastre vendas ou importe um OFX no Caixa.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b" style={{ borderColor: 'var(--app-border)' }}>
                          <th className="text-left py-2 px-2" style={{ color: 'var(--app-text-muted)' }}>Mês</th>
                          <th className="text-right py-2 px-2" style={{ color: 'var(--app-text-muted)' }}>Faturado</th>
                          <th className="text-right py-2 px-2" style={{ color: 'var(--app-text-muted)' }}>Recebido (OFX)</th>
                          <th className="text-right py-2 px-2" style={{ color: 'var(--app-text-muted)' }}>Recebido (Manual)</th>
                          <th className="text-right py-2 px-2" style={{ color: 'var(--app-text-muted)' }}>Gap</th>
                          <th className="text-center py-2 px-2" style={{ color: 'var(--app-text-muted)' }}>Fonte</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recebidoCalc.map((r) => (
                          <tr key={`${r.mes}_${r.ano}`} className="border-b hover:bg-white/5" style={{ borderColor: 'var(--app-border)' }} data-testid={`recebido-row-${r.mes}-${r.ano}`}>
                            <td className="py-2 px-2 capitalize" style={{ color: 'var(--app-text-primary)' }}>
                              {r.mes}/{r.ano}
                            </td>
                            <td className="py-2 px-2 text-right font-numbers" style={{ color: 'var(--app-text-primary)' }}>
                              {formatCurrency(r.faturamento)}
                            </td>
                            <td className="py-2 px-2 text-right font-numbers" style={{ color: r.recebido_ofx > 0 ? 'var(--color-primary)' : 'var(--app-text-muted)' }}>
                              {r.recebido_ofx > 0 ? formatCurrency(r.recebido_ofx) : '—'}
                            </td>
                            <td className="py-2 px-2 text-right font-numbers" style={{ color: 'var(--app-text-muted)' }}>
                              {r.recebido_manual != null ? formatCurrency(r.recebido_manual) : '—'}
                            </td>
                            <td className="py-2 px-2 text-right font-numbers font-bold" style={{ color: r.gap > 0 ? '#F59E0B' : r.gap < 0 ? '#EF4444' : 'var(--app-text-primary)' }}>
                              {formatCurrency(r.gap)}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {r.fonte === 'ofx' && (
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>OFX</span>
                              )}
                              {r.fonte === 'manual' && (
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>Manual</span>
                              )}
                              {r.fonte === 'sem_dados' && (
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>Pendente</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lista de overrides manuais cadastrados (histórico) */}
            {recebidos.length > 0 && (
              <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4 md:p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--app-text-muted)' }}>
                    Overrides manuais cadastrados
                  </p>
                  <div className="space-y-1">
                    {recebidos.map((rec) => (
                      <div key={rec.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-white/5 group">
                        <div className="flex-1">
                          <span className="text-sm capitalize" style={{ color: 'var(--app-text-primary)' }}>
                            {rec.mes}/{rec.ano}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium" style={{ color: '#F59E0B' }}>
                            {formatCurrency(rec.valor_recebido)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteRecebido(rec.id)}
                            data-testid={`delete-recebido-${rec.id}`}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Modal Nova Venda */}
        <Dialog open={showVendaModal} onOpenChange={(open) => {
          setShowVendaModal(open);
          if (!open) { setEditingVenda(null); resetVendaForm(); }
        }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <DialogHeader>
              <DialogTitle style={{ color: 'var(--app-text-primary)' }}>
                {editingVenda ? 'Editar Venda' : 'Nova Venda'} - {filtroMes}/{filtroAno}
              </DialogTitle>
              <DialogDescription style={{ color: 'var(--app-text-muted)' }}>
                Registre uma venda/faturamento por competencia
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Fonte de Receita</Label>
                <Select 
                  value={vendaForm.source_type} 
                  onValueChange={(v) => {
                    const tipo = types.find(t => t.name === v);
                    // So aplica os defaults da fonte se o usuario ainda nao escolheu nenhuma fonte
                    // (evita sobrescrever silenciosamente parcelas/antecipacao ja preenchidas)
                    const jaTinhaFonte = !!vendaForm.source_type;
                    setVendaForm({ 
                      ...vendaForm, 
                      source_type: v,
                      perfil_recebimento: jaTinhaFonte ? vendaForm.perfil_recebimento : (tipo?.perfil_padrao || vendaForm.perfil_recebimento),
                      numero_parcelas: jaTinhaFonte ? vendaForm.numero_parcelas : (tipo?.numero_parcelas_padrao || vendaForm.numero_parcelas),
                      antecipado: jaTinhaFonte ? vendaForm.antecipado : (tipo?.antecipado_padrao ?? vendaForm.antecipado)
                    });
                  }}
                >
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue placeholder="Selecione a fonte" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    {types.map((type) => (
                      <SelectItem key={type.id || type.name} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Valor Vendido (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={vendaForm.amount}
                  onChange={(e) => setVendaForm({ ...vendaForm, amount: e.target.value })}
                  placeholder="0,00"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>
              
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Perfil de Recebimento</Label>
                <Select 
                  value={vendaForm.perfil_recebimento} 
                  onValueChange={(v) => setVendaForm({ ...vendaForm, perfil_recebimento: v })}
                >
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    {PERFIS_RECEBIMENTO.map((perfil) => (
                      <SelectItem key={perfil.value} value={perfil.value}>
                        {perfil.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                  Define quando o valor sera recebido
                </p>
              </div>
              
              {vendaForm.perfil_recebimento === 'parcelado' && (
                <div className="p-3 rounded-lg space-y-3" style={{ backgroundColor: 'var(--app-background)', border: '1px solid var(--app-border)' }}>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Número de Parcelas</Label>
                    <Input
                      type="number"
                      min="2"
                      max="12"
                      value={vendaForm.numero_parcelas}
                      onChange={(e) => setVendaForm({ ...vendaForm, numero_parcelas: e.target.value })}
                      placeholder="Ex: 3"
                      style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                      data-testid="venda-numero-parcelas-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Recebimento é antecipado?</Label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        type="button"
                        variant={vendaForm.antecipado ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        style={vendaForm.antecipado ? { backgroundColor: 'var(--color-primary)' } : {}}
                        onClick={() => setVendaForm({ ...vendaForm, antecipado: true })}
                        data-testid="venda-antecipado-sim-btn"
                      >
                        <Zap size={14} className="mr-1" /> Sim, antecipado
                      </Button>
                      <Button
                        type="button"
                        variant={!vendaForm.antecipado ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        style={!vendaForm.antecipado ? { backgroundColor: 'var(--color-primary)' } : {}}
                        onClick={() => setVendaForm({ ...vendaForm, antecipado: false })}
                        data-testid="venda-antecipado-nao-btn"
                      >
                        Não, por parcela
                      </Button>
                    </div>
                    <p className="text-xs mt-2" style={{ color: 'var(--app-text-muted)' }} data-testid="venda-perfil-preview-text">
                      {vendaForm.antecipado
                        ? `O valor total cai no caixa de uma só vez (antecipação de recebíveis via cartão/factoring), mesmo vendido em ${vendaForm.numero_parcelas || 2}x.`
                        : `Cada parcela cai no caixa no seu próprio mês: ${vendaForm.numero_parcelas || 2} meses consecutivos a partir da venda.`}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Descricao (opcional)</Label>
                <Input
                  value={vendaForm.description}
                  onChange={(e) => setVendaForm({ ...vendaForm, description: e.target.value })}
                  placeholder="Ex: Mensalidades agosto"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>

              <div className="border-t pt-4" style={{ borderColor: 'var(--app-border)' }}>
                <p className="text-xs mb-3 flex items-center gap-1" style={{ color: 'var(--app-text-muted)' }}>
                  <Info size={12} />
                  Opcional: informe se houve antecipacao ou diferenca no recebimento
                </p>
                
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Recebido Real (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={vendaForm.recebido_real}
                    onChange={(e) => setVendaForm({ ...vendaForm, recebido_real: e.target.value })}
                    placeholder="Deixe vazio se igual ao vendido"
                    style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowVendaModal(false); setEditingVenda(null); resetVendaForm(); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveVenda} style={{ backgroundColor: 'var(--color-primary)' }}>
                  {editingVenda ? 'Salvar' : 'Cadastrar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Nova Fonte */}
        <Dialog open={showTipoModal} onOpenChange={(open) => {
          setShowTipoModal(open);
          if (!open) { setEditingTipo(null); resetTipoForm(); }
        }}>
          <DialogContent className="max-w-md" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <DialogHeader>
              <DialogTitle style={{ color: 'var(--app-text-primary)' }}>
                {editingTipo ? 'Editar Fonte' : 'Nova Fonte de Receita'}
              </DialogTitle>
              <DialogDescription style={{ color: 'var(--app-text-muted)' }}>
                Fontes representam categorias de onde vem sua receita
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Nome da Fonte *</Label>
                <Input
                  value={tipoForm.name}
                  onChange={(e) => setTipoForm({ ...tipoForm, name: e.target.value })}
                  placeholder="Ex: Mensalidades, Avulsas, Produtos"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>
              
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Cor</Label>
                <div className="flex gap-2 mt-1">
                  {COLORS.map((cor) => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setTipoForm({ ...tipoForm, color: cor })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${tipoForm.color === cor ? 'scale-110 border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: cor }}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Perfil de Recebimento Padrao</Label>
                <Select 
                  value={tipoForm.perfil_padrao} 
                  onValueChange={(v) => setTipoForm({ ...tipoForm, perfil_padrao: v })}
                >
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    {PERFIS_RECEBIMENTO.map((perfil) => (
                      <SelectItem key={perfil.value} value={perfil.value}>
                        {perfil.label} - {perfil.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {tipoForm.perfil_padrao === 'parcelado' && (
                <div className="p-3 rounded-lg space-y-3" style={{ backgroundColor: 'var(--app-background)', border: '1px solid var(--app-border)' }}>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Número de Parcelas Padrão</Label>
                    <Input
                      type="number"
                      min="2"
                      max="12"
                      value={tipoForm.numero_parcelas_padrao}
                      onChange={(e) => setTipoForm({ ...tipoForm, numero_parcelas_padrao: e.target.value })}
                      placeholder="Ex: 3"
                      style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                      data-testid="fonte-numero-parcelas-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Recebimento é antecipado por padrão?</Label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        type="button"
                        variant={tipoForm.antecipado_padrao ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        style={tipoForm.antecipado_padrao ? { backgroundColor: 'var(--color-primary)' } : {}}
                        onClick={() => setTipoForm({ ...tipoForm, antecipado_padrao: true })}
                        data-testid="fonte-antecipado-sim-btn"
                      >
                        <Zap size={14} className="mr-1" /> Sim, antecipado
                      </Button>
                      <Button
                        type="button"
                        variant={!tipoForm.antecipado_padrao ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        style={!tipoForm.antecipado_padrao ? { backgroundColor: 'var(--color-primary)' } : {}}
                        onClick={() => setTipoForm({ ...tipoForm, antecipado_padrao: false })}
                        data-testid="fonte-antecipado-nao-btn"
                      >
                        Não, por parcela
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowTipoModal(false); setEditingTipo(null); resetTipoForm(); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveTipo} style={{ backgroundColor: 'var(--color-primary)' }}>
                  {editingTipo ? 'Salvar' : 'Cadastrar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Registrar Recebido Real */}
        <Dialog open={showRecebidoModal} onOpenChange={(open) => {
          setShowRecebidoModal(open);
          if (!open) resetRecebidoForm();
        }}>
          <DialogContent className="max-w-md" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <DialogHeader>
              <DialogTitle style={{ color: 'var(--app-text-primary)' }}>Registrar Recebido Real</DialogTitle>
              <DialogDescription style={{ color: 'var(--app-text-muted)' }}>
                Corrige o valor de entrada de caixa de um mês já fechado
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Mês *</Label>
                  <Select
                    value={recebidoForm.mes}
                    onValueChange={(v) => setRecebidoForm({ ...recebidoForm, mes: v })}
                  >
                    <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }} data-testid="recebido-mes-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                      {MESES.map((m) => (
                        <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Ano *</Label>
                  <Input
                    type="number"
                    value={recebidoForm.ano}
                    onChange={(e) => setRecebidoForm({ ...recebidoForm, ano: e.target.value })}
                    style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    data-testid="recebido-ano-input"
                  />
                </div>
              </div>

              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Valor Recebido (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={recebidoForm.valor_recebido}
                  onChange={(e) => setRecebidoForm({ ...recebidoForm, valor_recebido: e.target.value })}
                  placeholder="0,00"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                  data-testid="recebido-valor-input"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                  Valor que efetivamente entrou no banco neste mês
                </p>
              </div>

              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Fonte (opcional)</Label>
                <Input
                  value={recebidoForm.fonte}
                  onChange={(e) => setRecebidoForm({ ...recebidoForm, fonte: e.target.value })}
                  placeholder="Ex: Banco X, PIX, Cartão"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>

              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Observação (opcional)</Label>
                <Input
                  value={recebidoForm.observacao}
                  onChange={(e) => setRecebidoForm({ ...recebidoForm, observacao: e.target.value })}
                  placeholder="Anotações..."
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowRecebidoModal(false); resetRecebidoForm(); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveRecebido} style={{ backgroundColor: 'var(--color-primary)' }} data-testid="save-recebido-btn">
                  Registrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
